import { type ReactNode, RefObject, createElement, createRef } from "react"

import type { TTFunction, TTranslationKeys } from "$/i18n/i18n"

import { ETypographyAlignment, ETypographyColor, ETypographySize, type TBadgeProps } from "@/3514/components"
// WISER-5404; somehow EBadgeVariant import breaks admin chapter-edit page loading
import { EBadgeVariant } from "@/3514/components/shared/badge/badge.types"

import {
    JourneyAchievementCurrentStreakIcon as CurrentStreakIcon,
    JourneyAchievementLongestStreakIcon as LongestStreakIcon,
    JourneyAchievementSessionAttendanceIcon as SessionAttendanceIcon,
    JourneyAchievementStarsEarnedIcon as StarsEarnedIcon
} from "../assets"
import { participantHeaderHelperComponents } from "../components"
import { EJourneyAchievementType } from "../types"

import {
    EParticipantHeaderComponentLayout,
    EParticipantHeaderComponentName,
    type TParticipantHeaderComponentRenderProps
} from "./participant.header.ui.types"

const journeyAchievementsLabelTranslationsMap: { [K in EJourneyAchievementType]: keyof TTranslationKeys } = {
    [EJourneyAchievementType.SessionAttendance]:
        "participantSide.dashboard.header.journeyAchievementsBlock.sessionAttendanceItem.label",
    [EJourneyAchievementType.CurrentStreak]:
        "participantSide.dashboard.header.journeyAchievementsBlock.currentStreakItem.label",
    [EJourneyAchievementType.LongestStreak]:
        "participantSide.dashboard.header.journeyAchievementsBlock.longestStreakItem.label",
    [EJourneyAchievementType.StarsEarned]: "participantSide.dashboard.header.journeyAchievementsBlock.starsEarned.label"
}

const journeyAchievementIconsMap: { [K in EJourneyAchievementType]: ReactNode } = {
    [EJourneyAchievementType.SessionAttendance]: createElement(SessionAttendanceIcon),
    [EJourneyAchievementType.CurrentStreak]: createElement(CurrentStreakIcon),
    [EJourneyAchievementType.LongestStreak]: createElement(LongestStreakIcon),
    [EJourneyAchievementType.StarsEarned]: createElement(StarsEarnedIcon)
}

const journeyAchievementBadgeVariantsMap: { [K in EJourneyAchievementType]: EBadgeVariant } = {
    [EJourneyAchievementType.SessionAttendance]: EBadgeVariant.Lightblue,
    [EJourneyAchievementType.CurrentStreak]: EBadgeVariant.DarkGreen,
    [EJourneyAchievementType.LongestStreak]: EBadgeVariant.LightGreen,
    [EJourneyAchievementType.StarsEarned]: EBadgeVariant.Yellow
}

const journeyAchievementBadgeTextsMap: {
    [K in EJourneyAchievementType]: (value: number, t: TTFunction) => Pick<TBadgeProps, "accentText" | "optionalText">
} = {
    [EJourneyAchievementType.SessionAttendance]: (
        value: number,
        _t: TTFunction
    ): Pick<TBadgeProps, "accentText" | "optionalText"> => ({
        accentText: value === -1 ? String() : String(value),
        optionalText: value === -1 ? "---" : "%"
    }),
    [EJourneyAchievementType.CurrentStreak]: (
        value: number,
        t: TTFunction
    ): Pick<TBadgeProps, "accentText" | "optionalText"> => ({
        accentText: String(value),
        optionalText: t("participantSide.dashboard.header.journeyAchievementsBlock.week", {
            context: value === 1 ? "" : "few"
        })
    }),
    [EJourneyAchievementType.LongestStreak]: (
        value: number,
        t: TTFunction
    ): Pick<TBadgeProps, "accentText" | "optionalText"> => ({
        accentText: String(value),
        optionalText: t("participantSide.dashboard.header.journeyAchievementsBlock.week", {
            context: value === 1 ? "" : "few"
        })
    }),
    [EJourneyAchievementType.StarsEarned]: (
        value: number,
        _t: TTFunction
    ): Pick<TBadgeProps, "accentText" | "optionalText"> => ({
        accentText: String(value),
        optionalText: undefined
    })
}

const headerComponentsRenderConfig: {
    [N in EParticipantHeaderComponentName]: {
        [L in EParticipantHeaderComponentLayout]: (
            rendererProps?: object // TODO: type TBU
        ) => TParticipantHeaderComponentRenderProps<Record<string, unknown>> // TODO: type TBU
    }
} = {
    [EParticipantHeaderComponentName.JourneyAchievements]: {
        [EParticipantHeaderComponentLayout.Page]: (): TParticipantHeaderComponentRenderProps => ({
            renderConfig: {
                layout: EParticipantHeaderComponentLayout.Page,
                componentWrapper: participantHeaderHelperComponents.journeyAchievementsComponentWrapper,
                contentWrapper: participantHeaderHelperComponents.journeyAchievementsContentWrapper
            }
        }),
        [EParticipantHeaderComponentLayout.AnalyticsModal]: (): TParticipantHeaderComponentRenderProps => ({
            renderConfig: {
                layout: EParticipantHeaderComponentLayout.AnalyticsModal,
                componentWrapper: participantHeaderHelperComponents.journeyAchievementsComponentWrapper,
                contentWrapper: participantHeaderHelperComponents.journeyAchievementsContentWrapper
            }
        })
    },
    [EParticipantHeaderComponentName.ModulesExcerpts]: {
        [EParticipantHeaderComponentLayout.Page]: (): TParticipantHeaderComponentRenderProps => ({
            renderConfig: {
                layout: EParticipantHeaderComponentLayout.Page,
                componentWrapper: participantHeaderHelperComponents.moduleExcerptsComponentWrapper,
                contentWrapper: participantHeaderHelperComponents.moduleExcerptsContentWrapper
            }
        }),
        [EParticipantHeaderComponentLayout.AnalyticsModal]: (): TParticipantHeaderComponentRenderProps<{
            moduleTitleSize: ETypographySize
            moduleTitleColor: ETypographyColor
        }> => ({
            renderConfig: {
                layout: EParticipantHeaderComponentLayout.AnalyticsModal,
                moduleTitleSize: ETypographySize.Tiny,
                moduleTitleColor: ETypographyColor.Black,
                componentWrapper: participantHeaderHelperComponents.moduleExcerptsComponentWrapper,
                contentWrapper: participantHeaderHelperComponents.moduleExcerptsContentWrapper
            }
        })
    },
    [EParticipantHeaderComponentName.NextSession]: {
        [EParticipantHeaderComponentLayout.Page]: (): TParticipantHeaderComponentRenderProps => ({
            renderConfig: {
                layout: EParticipantHeaderComponentLayout.Page,
                titleWrapper: participantHeaderHelperComponents.nextSessionTitleWrapper,
                componentWrapper: participantHeaderHelperComponents.nextSessionComponentWrapper,
                contentWrapper: participantHeaderHelperComponents.nextSessionContentWrapper
            }
        }),
        [EParticipantHeaderComponentLayout.AnalyticsModal]: (): TParticipantHeaderComponentRenderProps<{
            withDivider: boolean
            withCoachName: boolean
            avatarSize: number
            dayFontSize: ETypographySize
            dateFontSize: ETypographySize
            titleAlignment: ETypographyAlignment
        }> => ({
            renderConfig: {
                layout: EParticipantHeaderComponentLayout.AnalyticsModal,
                withDivider: true,
                withCoachName: true,
                avatarSize: 60,
                dayFontSize: ETypographySize.Medium,
                dateFontSize: ETypographySize.Medium,
                titleAlignment: ETypographyAlignment.Center,
                titleWrapper: participantHeaderHelperComponents.nextSessionTitleWrapper,
                componentWrapper: participantHeaderHelperComponents.nextSessionComponentWrapper,
                contentWrapper: participantHeaderHelperComponents.nextSessionContentWrapper
            }
        })
    }
}

const excerptsListRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>()

const config = {
    journeyAchievementsLabelTranslationsMap,
    journeyAchievementBadgeVariantsMap,
    journeyAchievementIconsMap,
    journeyAchievementBadgeTextsMap,
    headerComponentsRenderConfig,
    excerptsListRef
}

export { config as participantHeaderUiConfig }
